import React, { FC, HTMLAttributes, MouseEvent } from 'react'
import { Box, InputGroup, InputLeftElement, Input, Icon, IconButton, InputRightElement } from '@chakra-ui/core'
import { useLazyQuery } from '@apollo/client'
import { searchProductsByNameQuery } from '../../queries/index'
import { productsSearched } from '@engyalo/storefront-events'
import { IProduct, ISearchStats, IConfig } from '@engyalo/storefront-types/lib/plugins/model'
import {
  IProductsPricedPerCollectionVariables,
  IProductsPricedPerCollection,
  IProductsPricedPerCollectionCollection,
} from '@engyalo/storefront-types/lib/graphql/product'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { debounce, notEmpty } from '../../lib/utilities'
import { ProductList, ProductListFrom } from '../ProductList'
import { useParams, useHistory, useLocation } from 'react-router'
import { configState, orderConfigState, searchStats } from '../../recoil/atoms'
import { useRecoilValue, useRecoilState } from 'recoil'
import { ILocationSearch, skipIfInPreviewMode } from '../../utils/mode'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  placeholder?: string
  status?: boolean
  closeAction: (event: MouseEvent<HTMLButtonElement>) => void
}

export const Searchbar: FC<IProps> = ({ placeholder = 'Buscar producto', status, closeAction }: IProps) => {
  const [query, setQuery] = React.useState('')
  const history = useHistory()
  const location = useLocation<ILocationSearch>()
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const config = useRecoilValue<IConfig>(configState)
  const [currentSearchStats, setSearchStats] = useRecoilState<ISearchStats>(searchStats)

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  const [searchProducts, { data }] = useLazyQuery<IProductsPricedPerCollection, IProductsPricedPerCollectionVariables>(
    searchProductsByNameQuery,
    {
      onError(error) {
        const HTTPNOTFOUND = '404'
        const notFoundError = error.graphQLErrors.find((error) => error.extensions?.extensions?.code === HTTPNOTFOUND)

        if (notFoundError) {
          const newSearchCount = currentSearchStats.searchCount + 1
          skipIfInPreviewMode(() => {
            productsSearched({
              stats: {
                term: query,
                totalResults: 0,
                totalSearches: newSearchCount,
              },
              session,
            })
          })
          setSearchStats({ ...currentSearchStats, searchCount: newSearchCount })
        }
      },
      onCompleted(response) {
        if (query) {
          const newSearchCount = currentSearchStats.searchCount + 1
          skipIfInPreviewMode(() => {
            productsSearched({
              stats: {
                term: query,
                totalResults:
                  response.productsPricedPerCollection?.reduce(
                    (prev: number, collection: IProductsPricedPerCollectionCollection | null) =>
                      prev + (collection?.productsPriced?.length ?? 0),
                    0
                  ) || 0,
                totalSearches: newSearchCount,
              },
              session,
            })
          })
          setSearchStats({ ...currentSearchStats, searchCount: newSearchCount })
        }
      },
    }
  )
  const [products, setProducts] = React.useState<IProduct[]>([])

  const delayedQuery = React.useCallback(
    debounce((q) => searchProducts(q), 500),
    []
  )

  const { customer, order } = useParams()
  const searchInput = React.useRef<HTMLInputElement>(null)

  const onChange = (e: React.FormEvent<HTMLInputElement>): void => {
    const searchText = e.currentTarget.value
    setQuery(searchText)
    const search = new URLSearchParams(location.search)
    if (searchText) {
      search.set('q', searchText)
    } else {
      search.delete('q')
    }
    history.push({
      search: search.toString(),
    })
    delayedQuery({
      variables: {
        name: customer,
        configId: order,
        product: searchText,
      },
    })
  }

  React.useEffect(() => {
    if (query === '') {
      setProducts([])
    } else {
      const searchResult: IProduct[] = []
      data?.productsPricedPerCollection?.forEach((collection: IProductsPricedPerCollectionCollection | null) => {
        if (collection && collection.productsPriced) {
          const arr: IProduct[] = collection.productsPriced.filter(notEmpty).map((product) => ({
            ...product,
            enabledPrice: !!collection?.enabledPrice,
          }))
          searchResult.push(...arr)
        }
      })
      setProducts(searchResult)
    }
  }, [data, query])

  React.useEffect(() => {
    if (status && searchInput.current) {
      searchInput.current.focus()
    }
  }, [status])

  return (
    <Box width="100%" zIndex={100} position="relative">
      <InputGroup borderBottom="1px solid" borderColor="primary.500">
        <InputLeftElement h="40px" paddingX="16px">
          <Icon name="search" size="14px" />
        </InputLeftElement>
        <Input
          ref={searchInput}
          border="none"
          h="40px"
          paddingLeft="4rem"
          type="text"
          value={query}
          onChange={onChange}
          placeholder={placeholder}
        />
      </InputGroup>
      <InputRightElement paddingRight="4px">
        <IconButton
          width="32px"
          height="40px"
          background="transparent"
          borderColor="transparent"
          aria-label="cerrar"
          icon="close"
          onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
            const search = new URLSearchParams(location.search)
            search.delete('q')
            history.push({ search: search.toString() })
            closeAction(event)
          }}
        />
      </InputRightElement>
      <Box position="absolute" width="100%" backgroundColor="white">
        {products.length > 0 && <ProductList products={products} from={ProductListFrom.SEARCH} />}
      </Box>
    </Box>
  )
}
